import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_ROOT } from '../service/Services';
import { 
  HiOutlineCheckCircle, 
  HiOutlineXCircle, 
  HiOutlineClock, 
  HiOutlineUser, 
  HiOutlineCalendar,
  HiOutlineDocumentText,
  HiOutlineShoppingCart,
  HiOutlineTruck,
  HiOutlineRefresh,
  HiOutlineAdjustments
} from 'react-icons/hi';

export const DeliveryNoteApproval = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [deliveryNote, setDeliveryNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchDeliveryNote();
  }, [id]);

  const fetchDeliveryNote = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_ROOT}/delivery-note/${id}`);
      if (response.data.success) {
        setDeliveryNote(response.data.data);
      } else {
        setError('Delivery note not found');
      }
    } catch (error) {
      setError('Error fetching delivery note');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!window.confirm('Are you sure you want to approve this delivery note? This will update stock levels.')) {
      return;
    }

    try {
      setApproving(true);
      const response = await axios.get(`${API_ROOT}/delivery-note/approve/${id}`);
      
      if (response.data.success) {
        setSuccess(true);
        setDeliveryNote(prev => ({ ...prev, isApproved: true, status: 'Submitted' }));
        setTimeout(() => {
          navigate('/delivery-notes');
        }, 3000);
      } else {
        setError(response.data.message || 'Failed to approve');
      }
    } catch (error) {
      setError('Error approving delivery note');
      console.error('Error:', error);
    } finally {
      setApproving(false);
    }
  };

  const getTransactionTypeIcon = (type) => {
    switch(type?.toLowerCase()) {
      case 'purchase': return <HiOutlineShoppingCart className="text-blue-600" size={20} />;
      case 'sale': return <HiOutlineTruck className="text-green-600" size={20} />;
      case 'return': return <HiOutlineRefresh className="text-orange-600" size={20} />;
      case 'adjustment': return <HiOutlineAdjustments className="text-purple-600" size={20} />;
      default: return <HiOutlineDocumentText className="text-gray-600" size={20} />;
    }
  };

  const getStatusBadge = (status, isApproved) => {
    if (isApproved) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
          <HiOutlineCheckCircle className="mr-2" size={16} />
          Approved
        </span>
      );
    }
    
    switch(status?.toLowerCase()) {
      case 'pendingapproval':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
            <HiOutlineClock className="mr-2" size={16} />
            Pending Approval
          </span>
        );
      case 'submitted':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            <HiOutlineDocumentText className="mr-2" size={16} />
            Submitted
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
            <HiOutlineXCircle className="mr-2" size={16} />
            Cancelled
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
            Draft
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading delivery note...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <HiOutlineXCircle className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">Error</h3>
          <p className="mt-2 text-gray-600">{error}</p>
          <button
            onClick={() => navigate('/delivery-notes')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!deliveryNote) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <HiOutlineDocumentText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">Delivery Note Not Found</h3>
          <p className="mt-2 text-gray-600">The requested delivery note could not be found.</p>
          <button
            onClick={() => navigate('/delivery-notes')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {getTransactionTypeIcon(deliveryNote.transactionType)}
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Delivery Note: {deliveryNote.noteNumber}
                </h1>
                <p className="text-gray-600">Created on {new Date(deliveryNote.createdAt).toLocaleDateString()}</p>
              </div>
            </div>
            {getStatusBadge(deliveryNote.status, deliveryNote.isApproved)}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            {!deliveryNote.isApproved && deliveryNote.status === 'PendingApproval' && (
              <>
                <button
                  onClick={handleApprove}
                  disabled={approving}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {approving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Approving...
                    </>
                  ) : (
                    <>
                      <HiOutlineCheckCircle className="mr-2" size={16} />
                      Approve Delivery Note
                    </>
                  )}
                </button>
                <button
                  onClick={() => navigate('/delivery-notes')}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
              </>
            )}

            {/* View / Download PDF actions */}
            <button
              onClick={() => window.open(`/delivery-challan/print/${deliveryNote._id}`, '_self')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              View PDF
            </button>
            <button
              onClick={() => window.open(`/delivery-challan/print/${deliveryNote._id}?download=1`, '_blank')}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Download PDF
            </button>
          </div>

          {success && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
              <div className="flex items-center">
                <HiOutlineCheckCircle className="text-green-500 mr-2" size={20} />
                <span className="text-green-800 font-medium">
                  Delivery note approved successfully! Stock levels have been updated. Redirecting in 3 seconds...
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Customer & Sales Info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <HiOutlineUser className="mr-2" size={20} />
              Customer & Sales Information
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-500">Customer</label>
                <p className="text-gray-900">{deliveryNote.customerId?.name || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Sales Person</label>
                <p className="text-gray-900">{deliveryNote.salesPersonId?.name || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Delivery Date</label>
                <p className="text-gray-900">{deliveryNote.deliveryDate ? new Date(deliveryNote.deliveryDate).toLocaleDateString() : 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Transaction Info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <HiOutlineDocumentText className="mr-2" size={20} />
              Transaction Details
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-500">Transaction Type</label>
                <div className="flex items-center gap-2">
                  {getTransactionTypeIcon(deliveryNote.transactionType)}
                  <span className="text-gray-900 capitalize">{deliveryNote.transactionType || 'N/A'}</span>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <p className="text-gray-900">{deliveryNote.status || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Remarks</label>
                <p className="text-gray-900">{deliveryNote.remarks || 'No remarks'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Items</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {deliveryNote.items?.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {item.itemId?.itemName || 'Unknown Item'}
                        </div>
                        <div className="text-sm text-gray-500">
                          Code: {item.itemId?.itemCode || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.quantity}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ₹{item.rate || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ₹{((item.rate || 0) * (item.quantity || 0)).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Total */}
          <div className="mt-4 text-right">
            <div className="text-lg font-semibold text-gray-900">
              Total: ₹{deliveryNote.items?.reduce((sum, item) => sum + ((item.rate || 0) * (item.quantity || 0)), 0).toFixed(2)}
            </div>
          </div>
        </div>

        {/* Approval History */}
        {deliveryNote.isApproved && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Approval History</h3>
            <div className="flex items-center gap-3">
              <HiOutlineCheckCircle className="text-green-500" size={20} />
              <div>
                <p className="text-gray-900 font-medium">Approved</p>
                <p className="text-sm text-gray-500">
                  {deliveryNote.approvedAt ? new Date(deliveryNote.approvedAt).toLocaleString() : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryNoteApproval;
